import { ButtonProps, Grid, GridItem } from '@chakra-ui/react';
import {
  AccountTypeDisplayText,
  CustomFieldEntityEnum,
  IAccountRespSchema,
} from '@monetize/types/app';
import { ROUTES } from '../../../constants';
import { AccountStatusDisplayText } from '../../../constants/accounts';
import { ACCOUNTS_READ_ROLES } from '../../../services/acl/roles';
import { getAddress } from '../../../utils/address';
import { CustomFieldDataForm } from '../../CustomFields/CustomFieldDataForm';
import { BlankTextOrContent } from '../BlankTextOrContent';
import { MFormFieldReadOnly } from '../MFormFieldReadOnly';
import { LookupTriggerContent } from './LookupTriggerContent';
import { RecordLookupPopover } from './RecordLookupPopover';
import { RecordLookupPopoverHeader } from './RecordLookupPopoverHeader';

export interface AccountLookupPopoverProps {
  value: string;
  displayLabel?: string;
  /** Shows only the account name in the trigger, keeping it to a single line. */
  hideRecordId?: boolean;
  getRouteFn?: (record: IAccountRespSchema) => string;
  /**
   * Overrides the default label-over-id trigger. Pass this where that two-line
   * shape doesn't fit — e.g. a single-line header strip that shows only the
   * account name.
   */
  children?: React.ReactNode;
  /** Merged over the trigger button's defaults */
  buttonProps?: ButtonProps;
}

export function AccountLookupPopover({
  value,
  displayLabel,
  hideRecordId,
  getRouteFn = (record) => ROUTES.getAccountDetailRoute(record.id),
  children,
  buttonProps,
}: AccountLookupPopoverProps) {
  if (!value) {
    return null;
  }
  return (
    <RecordLookupPopover
      recordId={value}
      endpoint={`/api/accounts/${value}`}
      buttonProps={{ lineClamp: 3, ...buttonProps }}
      renderRecord={(record) => {
        const account = record as IAccountRespSchema;
        return (
          <>
            <RecordLookupPopoverHeader
              recordId={account.id}
              href={getRouteFn(account)}
              recordName={account.accountName}
              displayStatus={AccountStatusDisplayText[account.status]}
              roles={ACCOUNTS_READ_ROLES}
            />

            <Grid gridTemplateColumns="1fr 1fr" rowGap={4} mt={4}>
              <GridItem>
                <MFormFieldReadOnly label="Type">
                  {AccountTypeDisplayText[account.accountType]}
                </MFormFieldReadOnly>
              </GridItem>
              <GridItem>
                <MFormFieldReadOnly label="Status">
                  {AccountStatusDisplayText[account.status]}
                </MFormFieldReadOnly>
              </GridItem>
              <GridItem colSpan={2}>
                <MFormFieldReadOnly label="Billing Address">
                  <BlankTextOrContent value={account.billingAddress}>
                    {getAddress(account.billingAddress).fullAddress}
                  </BlankTextOrContent>
                </MFormFieldReadOnly>
              </GridItem>
              <GridItem colSpan={2}>
                <MFormFieldReadOnly label="Shipping Address">
                  <BlankTextOrContent value={account.shippingAddress}>
                    {getAddress(account.shippingAddress).fullAddress}
                  </BlankTextOrContent>
                </MFormFieldReadOnly>
              </GridItem>
            </Grid>

            <CustomFieldDataForm
              entity={CustomFieldEntityEnum.ACCOUNT}
              value={account.customFields}
              isReadOnly
              variant="readonly"
              setValue={() => {}}
              fieldColSpan={6}
              mode="modal"
            />
          </>
        );
      }}
    >
      {children ?? (
        <LookupTriggerContent
          displayLabel={displayLabel}
          value={value}
          hideRecordId={hideRecordId}
        />
      )}
    </RecordLookupPopover>
  );
}
